import { useEffect, useMemo, useState } from "react";
import { useForfait } from "hooks/useForfait";
import { useSite } from "hooks/useSite";
import { cmsDesSites } from "utils";

export type SitesDuForfait = {
  // Tous les sites, déjà lus pour le formulaire
  tous: SiteType[];
  // false tant que les sites du forfait choisi ne sont pas connus
  pret: boolean;
  // Sites proposés : ceux du forfait, plus ceux qu'une intervention existante a gardés
  sites: SiteType[];
  // Cochés à l'ouverture : la sélection d'une intervention existante, sinon les sites actifs du forfait
  parDefaut: string[];
  // CMS à l'ouverture : celui d'une intervention existante, sinon celui des sites cochés
  cmsParDefaut: string;
};

export function useSitesDuForfait(idForfait?: string, intervention?: InterventionType | null): SitesDuForfait {
  const { getSites } = useSite();
  const { getForfaitField } = useForfait();
  const [tous, setTous] = useState<SiteType[] | null>(null);
  const [forfait, setForfait] = useState<{ id: string; idsites: string[] } | null>(null);

  useEffect(() => {
    getSites()
      .then(setTous)
      .catch((error) => {
        console.error(error);
        setTous([]);
      });
  }, [getSites]);

  useEffect(() => {
    if (!idForfait) {
      return;
    }
    let cancelled = false;
    Promise.resolve(getForfaitField(idForfait, "idsites"))
      .then((idsites: unknown) => {
        if (!cancelled) {
          setForfait({ id: idForfait, idsites: Array.isArray(idsites) ? idsites : [] });
        }
      })
      .catch((error: unknown) => {
        console.error(error);
        if (!cancelled) {
          setForfait({ id: idForfait, idsites: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [idForfait, getForfaitField]);

  const pret = Boolean(idForfait) && tous !== null && forfait?.id === idForfait;
  // Une intervention garde ses sites tant qu'elle reste sur son forfait
  const memeForfait = Boolean(intervention) && intervention?.idforfait === idForfait;
  const gardes = memeForfait ? intervention?.idsites : undefined;
  return useMemo(() => {
    const liste = tous ?? [];
    if (!pret) {
      return { tous: liste, pret: false, sites: [], parDefaut: [], cmsParDefaut: "" };
    }
    const ids = Array.from(new Set([...forfait.idsites, ...(gardes ?? [])]));
    const sites = ids.map((id) => liste.find((site) => site.id === id)).filter(Boolean) as SiteType[];
    const actifs = sites.filter((site) => forfait.idsites.includes(site.id) && site.actif);
    const parDefaut = gardes ?? (actifs.length > 0 ? actifs : sites).map((site) => site.id);
    const coches = sites.filter((site) => parDefaut.includes(site.id));
    const cmsParDefaut = memeForfait && intervention?.cms ? intervention.cms : cmsDesSites(coches);
    return { tous: liste, pret: true, sites, parDefaut, cmsParDefaut };
  }, [tous, pret, forfait, gardes, memeForfait, intervention?.cms]);
}
